/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { isError } from "@epic-mw/error-types";
import useSWRImmutable from "swr/immutable";

import { useApiConfig } from "../api-config";
import { getApiPath } from "../utils";

/*
  Client-side fallback values, used only when the `/launch-darkly-flags` fetch fails and
  `window._epicFlags` is unset.

  `epicgames-use-cosmos` was the sole entry and is gone — it gated cosmos-vs-legacy origin
  selection at the edge, was on in production, and the unified-platform cutover removed the
  gate everywhere. `editor-user-agent-redirect` takes its place as the one flag the router's
  `LaunchDarklyFlags` union still carries, so this stays a real map rather than becoming `{}`
  (which would make `keyof` resolve to `never` and leave `getFlag`/`useFlag` uncallable).

  `false` is the safe fallback: the flag causes a redirect away from the site, so failing
  closed means failing to redirect rather than redirecting on a failed flag fetch.

  NOTE: `getFlag` and `useFlag` currently have no callers anywhere in the monorepo. If that is
  still true next time someone touches this, delete the module rather than curate the map.
*/
const defaultLaunchDarklyFlagValues = {
	"editor-user-agent-redirect": false,
};

export const getFlag = async <T extends keyof typeof defaultLaunchDarklyFlagValues>(
	flag: T,
	serverUrl?: string,
): Promise<(typeof defaultLaunchDarklyFlagValues)[T]> => {
	let flags = (window as any)._epicFlags as typeof defaultLaunchDarklyFlagValues | undefined;

	if (!flags) {
		try {
			const response = await fetch(`${getApiPath(serverUrl)}/launch-darkly-flags`);
			flags = await response.json();
		} catch (err) {
			console.error("flags() failed to load", isError(err) && err.message);
		}
	}

	if (!flags) {
		return defaultLaunchDarklyFlagValues[flag];
	}
	return flags[flag];
};

export const useFlag = <T extends keyof typeof defaultLaunchDarklyFlagValues>(
	flag: T,
): {
	value?: (typeof defaultLaunchDarklyFlagValues)[T];
	isLoading: boolean;
	error?: string;
} => {
	const { serverUrl } = useApiConfig();
	const { data, error } = useSWRImmutable([`flags-${flag}`, serverUrl], () => getFlag(flag, serverUrl), {
		errorRetryCount: 1,
	});

	return {
		value: data,
		isLoading: !data && !error,
		error,
	};
};
